// Dictation: browser speech recognition in short self-restarting
// bursts (continuous mode is unreliable outside Chrome). Reports
// its state through a status callback so failures are named.

const Recognition: any =
  (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;

export const dictationSupported: boolean = Boolean(Recognition);

const DICTATE_LANG: Record<string, string> = { en: "en-IN", ta: "ta-IN", ml: "ml-IN" };

export interface DictationUI {
  button: HTMLButtonElement;
  getLang(): string;
  onText(text: string): void;
  onStatus(msg: string, isError?: boolean): void;
}

let recognizer: any = null;
let dictating = false;
let emptyBursts = 0;

/** Update the button's label span, leaving its icon intact. */
const setLabel = (btn: HTMLButtonElement, text: string) => {
  const label = btn.querySelector(".btn-label");
  if (label) label.textContent = text;
  else btn.textContent = text;
};

function stopUI(ui: DictationUI) {
  recognizer = null;
  ui.button.classList.remove("listening");
  setLabel(ui.button, "speak your note");
}

export function toggleDictation(ui: DictationUI): void {
  if (dictating) {
    dictating = false;
    ui.onStatus("");
    recognizer?.stop();
    return;
  }
  dictating = true;
  emptyBursts = 0;
  ui.button.classList.add("listening");
  setLabel(ui.button, "listening… tap to stop");
  ui.onStatus("listening — start speaking…");
  listenOnce(ui);
}

function listenOnce(ui: DictationUI): void {
  let heardSomething = false;
  try {
    recognizer = new Recognition();
    recognizer.lang = DICTATE_LANG[ui.getLang()] ?? "en-IN";
    recognizer.continuous = false;
    recognizer.interimResults = true;
    recognizer.maxAlternatives = 1;
  } catch {
    dictating = false;
    stopUI(ui);
    ui.onStatus("dictation isn't available in this browser — it works best in Chrome or Edge", true);
    return;
  }

  recognizer.onresult = (e: any) => {
    heardSomething = true;
    const results = [...e.results] as any[];
    const interim = results.filter((r) => !r.isFinal).map((r) => r[0].transcript).join(" ").trim();
    if (interim) ui.onStatus(`hearing: “${interim}”`);
    const text = results.filter((r) => r.isFinal).map((r) => r[0].transcript).join(" ").trim();
    if (text) {
      ui.onText(text);
      ui.onStatus("listening — keep going or tap stop…");
    }
  };

  recognizer.onerror = (e: any) => {
    if (e.error === "no-speech" || e.error === "aborted") return;
    dictating = false;
    const why: Record<string, string> = {
      "not-allowed": "microphone permission was denied — check the site's mic permission",
      "service-not-allowed": "this browser blocks its speech service — try Chrome or Edge",
      "audio-capture": "no microphone was found",
      network: "the speech service couldn't be reached — it needs Chrome/Edge and internet",
      "language-not-supported": "this browser can't recognise that language — try Chrome",
    };
    ui.onStatus(`dictation stopped — ${why[e.error] || e.error}`, true);
  };

  recognizer.onend = () => {
    if (!dictating) {
      stopUI(ui);
      return;
    }
    emptyBursts = heardSomething ? 0 : emptyBursts + 1;
    if (emptyBursts >= 3) {
      dictating = false;
      stopUI(ui);
      ui.onStatus(
        "the mic seems live but nothing is recognised — dictation works best in Chrome or Edge",
        true
      );
      return;
    }
    setTimeout(() => dictating && listenOnce(ui), 250);
  };

  try {
    recognizer.start();
  } catch (e: any) {
    dictating = false;
    stopUI(ui);
    ui.onStatus(`couldn't start dictation — ${e.message}`, true);
  }
}
